/**
 * O nome de uma pessoa como as TELAS o mostram, a partir do nome cru que o
 * backend devolve (snapshot do cadastro, em geral CAIXA ALTA e completo —
 * "MARIA EDUARDA SOUZA LIMA"). Só apresentação: nome civil, nome de embarque,
 * documentos, checkout e voucher seguem com o nome inteiro como veio.
 *
 * A lista de partículas é pt-BR por design mesmo fora desse locale: o nome que
 * o backend manda é sempre em português, então "da"/"dos"/"e" continuam sendo
 * as partículas certas em qualquer sessão — só o CASING (`toLocale*Case`)
 * segue o idioma ativo, que o host passa em `locale`.
 */

const PARTICLES = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);

/** Sufixos de filiação: andam colados ao sobrenome no nome curto
 *  ("JOÃO CARLOS SOUZA FILHO" → "João Souza Filho", não "João Filho"). */
const SUFFIXES = new Set(['filho', 'filha', 'júnior', 'junior', 'neto', 'neta']);

function isSuffix(word: string, locale: string): boolean {
  return SUFFIXES.has(word.toLocaleLowerCase(locale));
}

/** Cada palavra em caixa própria; partículas minúsculas, menos a primeira. */
export function formatPersonName(raw: string, locale = 'pt-BR'): string {
  return raw
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) => {
      const lower = word.toLocaleLowerCase(locale);
      return index > 0 && PARTICLES.has(lower)
        ? lower
        : lower.charAt(0).toLocaleUpperCase(locale) + lower.slice(1);
    })
    .join(' ');
}

/** "Nome Sobrenome": o primeiro nome e o último sobrenome (mais o sufixo de
 *  filiação, quando há). Um nome só, ou dois, voltam inteiros. */
export function shortPersonName(raw: string, locale = 'pt-BR'): string {
  const words = formatPersonName(raw, locale).split(' ').filter(Boolean);
  if (words.length <= 2) return words.join(' ');
  const first = words[0] ?? '';
  const last = words.at(-1) ?? '';
  if (isSuffix(last, locale)) {
    return `${first} ${words.at(-2) ?? ''} ${last}`;
  }
  return `${first} ${last}`;
}

/** As iniciais do nome CURTO, sem o sufixo — "MS" para "Marina Souza", "JS"
 *  para "João Souza Filho", "J" para um nome só. */
export function personInitials(raw: string, locale = 'pt-BR'): string {
  const words = shortPersonName(raw, locale).split(' ').filter(Boolean);
  // O sufixo só sai quando sobra alguma palavra para virar inicial: quem se
  // chama só "NETO" continua com "N" em vez de um avatar vazio.
  const named = words.filter((word) => !isSuffix(word, locale));
  return (
    (named.length > 0 ? named : words)
      .slice(0, 2)
      // `Array.from`: uma letra fora do BMP ocupa dois code units; `charAt(0)`
      // devolveria meio par substituto.
      .map((word) => (Array.from(word)[0] ?? '').toLocaleUpperCase(locale))
      .join('')
  );
}
